import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { Loader, Overline, Breadcrumbs } from "@/components/shared";
import { DealCard } from "@/components/Ads";

const RETAILERS = ["All", "Amazon", "eBay", "Walmart"];
const COUNTRIES = ["All", "US", "UK"];
const CATEGORIES = ["All", "AI & Software", "Tech", "Gaming", "Electronics", "Home", "Accessories"];

function FilterRow({ label, options, value, onChange, prefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-mono text-slate-500 w-20 shrink-0">{label}</span>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} data-testid={`${prefix}-${o.replace(/[^a-zA-Z]/g, "")}`}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${value === o ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>{o}</button>
      ))}
    </div>
  );
}

export default function Deals() {
  const [items, setItems] = useState(null);
  const [retailer, setRetailer] = useState("All");
  const [country, setCountry] = useState("All");
  const [category, setCategory] = useState("All");
  useSEO({ title: "Tech, Gaming & AI Deals", description: "Discover the latest deals on AI tools, technology, gaming gear and electronics — sourced from Amazon, Walmart and eBay." });
  useEffect(() => {
    setItems(null);
    let active = true;
    api.deals({ retailer, country, category }).then((d) => { if (active) setItems(d.items); });
    return () => { active = false; };
  }, [retailer, country, category]);

  return (
    <div className="klyroo-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Deals" }]} />
        <Overline>Save money</Overline>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Deals</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-lg">Discover the latest deals on AI tools, technology, gaming gear and electronics.</p>

        <div className="my-8 space-y-3">
          <FilterRow label="Retailer" options={RETAILERS} value={retailer} onChange={setRetailer} prefix="deal-retailer" />
          <FilterRow label="Country" options={COUNTRIES} value={country} onChange={setCountry} prefix="deal-country" />
          <FilterRow label="Category" options={CATEGORIES} value={category} onChange={setCategory} prefix="deal-cat" />
        </div>

        {!items ? <Loader /> : items.length === 0 ? (
          <div className="py-24 text-center" data-testid="deals-empty">
            <p className="text-slate-300 text-lg font-medium">No deals available right now</p>
            <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">We're refreshing the latest offers across Amazon, Walmart and eBay. Check back shortly or adjust your filters.</p>
          </div>
        ) : (
          <>
            <p className="text-xs font-mono text-slate-500 mb-4">{items.length} deal{items.length === 1 ? "" : "s"}</p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="deals-grid">
              {items.map((d) => <DealCard key={d.slug} d={d} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
